import React, { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import {
  LoginPage,
  Form,
  FormGroup,
  TextInput,
  Button,
  Alert
} from "@patternfly/react-core";
import CheckIcon from "@patternfly/react-icons/dist/esm/icons/check-icon";
import TimesIcon from "@patternfly/react-icons/dist/esm/icons/times-icon";
import backgroundImg from 'shared/assets/images/svg/pf-background.svg';
import zxcvbn from "zxcvbn";

const PasswordResetConfirmPage = (props) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const uid = searchParams.get("uid");
  const token = searchParams.get("token");

  const [password, setPassword] = useState("");
  const [password2, setPassword2] = useState("");
  const [alertText, setAlertText] = useState("");
  const [alertVariant, setAlertVariant] = useState("info"); // success, danger, warning, info
  const [showAlert, setShowAlert] = useState(false);
  const [buttonLabel, setButtonLabel] = useState("Ustaw nowe hasło");
  const [criteria, setCriteria] = useState({
    length: false,
    uppercase: false,
    lowercase: false,
    number: false,
    special: false,
    passwordsMatch: false,
    notCommon: false,
  });
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);
  
  // Walidacja live
  useEffect(() => {
    const result = zxcvbn(password);

    setCriteria({
      length: password.length >= 8,
      uppercase: /[A-Z]/.test(password),
      lowercase: /[a-z]/.test(password),
      number: /\d/.test(password),
      special: /[!@#$%^&*()_+\-=[\]{}|;:,.<>?]/.test(password),
      passwordsMatch: password && password === password2,
      notCommon: result.score >= 3,
    });
  }, [password, password2]);

  const handleSubmit = async (event) => {
    event.preventDefault();

    // Sprawdzenie pól
    if (!password || !password2) {
      setAlertText("Uzupełnij oba pola.");
      setAlertVariant("warning");
      setShowAlert(true);
      return;
    }

    if (password !== password2) {
      setAlertText("Hasła nie są takie same.");
      setAlertVariant("danger");
      setShowAlert(true);
      return;
    }

    if (
      !criteria.length ||
      !criteria.uppercase ||
      !criteria.lowercase ||
      !criteria.number ||
      !criteria.special ||
      !criteria.notCommon
    ) {
      setAlertText("Hasło nie spełnia wymagań bezpieczeństwa.");
      setAlertVariant("danger");
      setShowAlert(true);
      return;
    }

    setButtonLabel("Zapisywanie...");
    setIsButtonDisabled(true);

    try {
      const resp = await fetch(
        `${props.state.api_url}/ti/api/password-reset-confirm/`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ uid, token, new_password: password }),
        }
      );

      const data = await resp.json();

      if (!resp.ok) {
        // Token lub LDAP
        const isTokenError =
          data.detail?.toLowerCase().includes("token") ||
          data.detail?.toLowerCase().includes("invalid");
        setAlertText(
          isTokenError
            ? "Token jest nieważny lub wygasł. Wygeneruj nowy link resetujący."
            : data.detail || "Błąd LDAP — hasło nie mogło zostać zmienione."
        );
        setAlertVariant(isTokenError ? "warning" : "danger");
        setShowAlert(true);
        setButtonLabel("Ustaw nowe hasło");
        setIsButtonDisabled(false);
        return;
      }

      // Sukces
      setAlertText("Hasło zostało zmienione. Możesz się zalogować.");
      setAlertVariant("success");
      setShowAlert(true);
      setPassword("");
      setPassword2("");
      setButtonLabel("Zmieniono");

      setTimeout(() => {
        setButtonLabel("Ustaw nowe hasło");
        setIsButtonDisabled(false);
        setShowAlert(false);
        navigate("/auth");
      }, 9000);
    } catch (err) {
      setAlertText("Błąd serwera.");
      setAlertVariant("danger");
      setShowAlert(true);
      setButtonLabel("Ustaw nowe hasło");
      setIsButtonDisabled(false);
    }
  };

  return (
      <div className="pf-login-wrapper">
        <LoginPage
          // backgroundImgSrc={backgroundImg}
          loginTitle="Ustaw nowe hasło"
          loginSubtitle="Wprowadź nowe hasło"
        >
          <Form onSubmit={handleSubmit}>
            {showAlert && (
              <Alert
                variant={alertVariant}
                // isInline
                title={alertText}
                // style={{ marginBottom: "1rem" }}
              />
            )}

            <FormGroup label="Nowe hasło" isRequired fieldId="newPassword">
              <TextInput
                id="newPassword"
                autoComplete="new-password"
                type="password"
                value={password}
                onChange={(_, v) => setPassword(v)}
              />
            </FormGroup>

            <FormGroup label="Powtórz hasło" isRequired fieldId="confirmPassword">
              <TextInput
                id="confirmPassword"
                autoComplete="new-password"
                type="password"
                value={password2}
                onChange={(_, v) => setPassword2(v)}
              />
            </FormGroup>

            {/* Lista kryteriów */}
            <div style={{ marginTop: "1rem", fontSize: "0.9rem" }}>
              {[
                { key: "length", label: "Min. 8 znaków" },
                { key: "uppercase", label: "Co najmniej jedna wielka litera" },
                { key: "lowercase", label: "Co najmniej jedna mała litera" },
                { key: "number", label: "Co najmniej jedna cyfra" },
                { key: "special", label: "Co najmniej jeden znak specjalny" },
                { key: "notCommon", label: "Hasło nie jest łatwe do odgadnięcia" },
                { key: "passwordsMatch", label: "Hasła się zgadzają" },
              ].map((c) => (
                <div
                  key={c.key}
                  style={{
                    color: criteria[c.key] ? "green" : "red",
                    display: "flex",
                    alignItems: "center",
                    marginBottom: "0.2rem",
                  }}
                >
                  {criteria[c.key] ? <CheckIcon /> : <TimesIcon />}{" "}
                  <span style={{ marginLeft: "0.3rem" }}>{c.label}</span>
                </div>
              ))}
            </div>

            <Button
              variant="primary"
              type="submit"
              isDisabled={
                isButtonDisabled ||
                !criteria.length ||
                !criteria.uppercase ||
                !criteria.lowercase ||
                !criteria.number ||
                !criteria.special ||
                !criteria.notCommon ||
                !criteria.passwordsMatch
              }
              style={{ marginTop: "1rem" }}
            >
              {buttonLabel}
            </Button>
          </Form>
        </LoginPage>
      </div>
  );
};

export default PasswordResetConfirmPage;